import { action, makeObservable, observable } from "mobx";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  FieldsSettingsModel,
  Sidebar,
  TreeViewComponent,
  TreeViewModel,
} from "@syncfusion/ej2-react-navigations";
import {
  MaskedTextBoxComponent,
  MaskedTextBoxModel,
} from "@syncfusion/ej2-react-inputs";
import { NavigationTreeViewNode } from "../config/useNavigationTreeViewConfig";
import { DataManager, Query, Predicate } from "@syncfusion/ej2-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { utils } from "../../../../../";

export interface SearchResults {
  data: NavigationTreeViewNode[];
  expand: boolean;
}

type TreeViewComponentSettings = TreeViewModel & DefaultHtmlAttributes;
export type TreeViewSettings = {
  nodeTemplate?: string | Function | any;
} & Omit<TreeViewComponentSettings, "nodeTemplate">;

export interface NavigationTreeViewStoreProps {
  nodeTemplate?: string | Function | any;
}

export class NavigationTreeViewStore {
  // public
  public maskedTextBoxInstance?: MaskedTextBoxComponent | null = null;
  public treeViewInstance?: TreeViewComponent | null = null;
  public sideBarInstance?: Sidebar | null = null;
  public navigationFieldsConfig: NavigationTreeViewNode[] = [];

  private defaultMaskedTextBoxSettings: MaskedTextBoxModel &
    DefaultHtmlAttributes = {
    placeholder: "Search by Component Name...",
  };

  private nodeTemplate = (data: NavigationTreeViewNode): React.ReactNode => {
    return (
      <div className="navigation-template">
        <div className="navigation-template-icon">
          {data.icon && <FontAwesomeIcon icon={data.icon} />}
        </div>
        <div className="navigation-template-name ename">{data.name}</div>
      </div>
    );
  };

  private defaultTreeViewSettings: TreeViewSettings = {
    allowDragAndDrop: false,
    allowMultiSelection: false,
    nodeTemplate: this.nodeTemplate,
  };

  constructor() {
    makeObservable(this, {
      maskedTextBoxInstance: observable,
      treeViewInstance: observable,
      navigationFieldsConfig: observable,
      sideBarInstance: observable,
      setTreeViewFieldsConfig: action,
    });
  }

  public isOpen = () => {
    return this.sideBarInstance?.isOpen;
  };

  public open = () => {
    this.sideBarInstance?.show();
    this.treeViewInstance?.expandAll();
  };

  public close = () => {
    this.sideBarInstance?.hide();
    this.treeViewInstance?.collapseAll();
  };

  public toggle = () => {
    if (this.sideBarInstance?.isOpen) {
      this.sideBarInstance?.hide();
      this.treeViewInstance?.collapseAll();
    } else {
      this.sideBarInstance?.show();
      this.treeViewInstance?.expandAll();
    }
  };

  // Datasource
  private changeDataSource = (data: any) => {
    if (!this.treeViewInstance) {
      return;
    }

    this.treeViewInstance.fields = {
      dataSource: [...data],
      id: "id",
      text: "name",
      child: "fields",
      expanded: "expanded",
      parentID: "parentId",
      tooltip: "tooltipText",
      selected: "isSelected",
    };
  };

  private getItems = (
    property: string,
    value: string,
    list?: NavigationTreeViewNode[]
  ): NavigationTreeViewNode[] => {
    if (!(list && list.length > 0)) {
      return [];
    }
    const predicate = new Predicate(property, "contains", value, true);
    const filteredList: any = new DataManager(list).executeLocal(
      new Query().where(predicate)
    );

    return filteredList;
  };

  //search
  public search = (): SearchResults | undefined => {
    if (!this.maskedTextBoxInstance) {
      return;
    }

    const _text = this.maskedTextBoxInstance.element.value;

    if (_text == "") {
      // show all data
      this.changeDataSource([...this.navigationFieldsConfig]);
      return { data: [...this.navigationFieldsConfig], expand: true };
    }

    // Search children
    let results = utils.clone.deep<NavigationTreeViewNode[]>(
      this.navigationFieldsConfig
    );
    for (let i = 0; i < results.length; i++) {
      const child = results[i];
      const r = this.getItems("name", _text, child.fields);
      child.fields = child.fields?.filter((x: any) =>
        r.map((y) => y.id).includes(x.id)
      );
    }
    results = results.filter((x: any) => x.fields && x.fields.length > 0);

    this.changeDataSource([...results]);

    return { data: [...results], expand: true };
  };

  setTreeViewFieldsConfig = (
    componentTreeViewConfig: NavigationTreeViewNode[]
  ) => {
    this.navigationFieldsConfig = [...componentTreeViewConfig];
  };

  // Init
  public setup = (componentTreeViewConfig: NavigationTreeViewNode[]) => {
    // TreeView Settings
    const treeViewSettings = {
      ...this.defaultTreeViewSettings,
    } as TreeViewModel & DefaultHtmlAttributes;

    this.setTreeViewFieldsConfig(componentTreeViewConfig);

    treeViewSettings.fields = {
      dataSource: [...this.navigationFieldsConfig],
      id: "id",
      text: "name",
      child: "fields",
      expanded: "expanded",
      parentID: "parentId",
      tooltip: "tooltipText",
      selected: "isSelected",
    } as unknown as FieldsSettingsModel;

    const maskedTextBoxModel = {
      ...this.defaultMaskedTextBoxSettings,
    } as MaskedTextBoxModel & DefaultHtmlAttributes;

    return [maskedTextBoxModel, treeViewSettings];
  };

  public initialize = (
    newTreeViewInstance: TreeViewComponent,
    newSideBarInstance: Sidebar
  ) => {
    this.setInstance(newTreeViewInstance, newSideBarInstance);
  };
  public initializeSearchBox = (
    newMaskedTextBoxInstance: MaskedTextBoxComponent
  ) => {
    this.setInstanceSearchBox(newMaskedTextBoxInstance);
  };

  public reset = () => {
    this.setInstance(undefined, undefined);
  };

  public setInstance = (
    scopeTreeViewInstance?: TreeViewComponent,
    scopeSidebarInstance?: Sidebar
  ) => {
    this.treeViewInstance = scopeTreeViewInstance;
    this.sideBarInstance = scopeSidebarInstance;
  };

  public setInstanceSearchBox = (
    scopeMaskedTextBoxInstance?: MaskedTextBoxComponent
  ) => {
    this.maskedTextBoxInstance = scopeMaskedTextBoxInstance;
  };
}

export const navigationTreeViewStore = new NavigationTreeViewStore();
